import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { ChevronRight, Shield, Sparkles, KeyRound, Heart, Star, ArrowDown, DoorOpen } from "lucide-react";
import { useScroll, useTransform } from "framer-motion";
import api from "../lib/api";
import { motion, Reveal, StaggerContainer, CountUp, staggerItem, easeLuxury } from "../components/motion";
import MagneticButton from "../components/MagneticButton";
import TiltCard from "../components/TiltCard";
import Hero3D from "../components/Hero3D";
import Marquee from "../components/Marquee";
import { useContent } from "../context/SiteContentContext";
import { mediaUrl } from "../lib/api";

const IMG = {
  hero: "/Images/suite1-hero.png",
  lounge: "/Images/lounge1.png",
  kitchen: "/Images/kitchen1.png",
  bath: "/Images/washroom1.png",
  g1: "/Images/suite1-detail.png",
  g2: "/Images/lounge3.png",
  g3: "/Images/kitchen2.png",
};

export default function HomePage() {
  const { get } = useContent();
  const scenes = (get("home.scenes") || []).map((sc, i) => ({ ...sc, n: String(i + 1).padStart(2, "0") }));
  const [reviews, setReviews] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const heroFade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/reviews").then((r) => {
      const data = Array.isArray(r.data) ? r.data : (r.data?.results ?? []);
      setReviews(data.slice(0, 3));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* ============ CINEMATIC HERO ============ */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center grain overflow-hidden"
        style={{ background: "#070605" }}
      >
        {/* Crimson room-glow — the light spilling through the door of the mark */}
        <div
          className="absolute pointer-events-none animate-red-breath"
          style={{
            top: "18%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(900px, 90vw)",
            height: "600px",
            background:
              "radial-gradient(ellipse at center, rgba(155,27,27,0.28) 0%, rgba(110,18,18,0.10) 40%, transparent 72%)",
            filter: "blur(40px)",
            zIndex: 1,
          }}
        />

        {/* Faint hero image, deep in the back for texture */}
        <div className="absolute inset-0" style={{ zIndex: 0 }}>
          <img src={mediaUrl(get("home.hero_image"))} alt="" className="w-full h-full object-cover opacity-[0.14] animate-ken-burns" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,6,5,0.7) 0%, rgba(7,6,5,0.5) 40%, rgba(7,6,5,0.95) 100%)" }} />
        </div>

        {/* 3D layer — imperative Three.js, no R3F */}
        <div className="absolute inset-0" style={{ zIndex: 2 }}>
          <Hero3D />
        </div>

        {/* Content */}
        <motion.div
          className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full pt-32 pb-24 text-center"
          style={{ zIndex: 3, y: heroTextY, opacity: heroFade }}
        >
          {/* Logo mark */}
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: easeLuxury }}
          >
            <img
              src="/logo (2).png"
              alt="Room XIII"
              style={{ width: "clamp(180px, 28vw, 340px)", filter: "drop-shadow(0 0 40px rgba(155,27,27,0.35))" }}
            />
          </motion.div>


          <motion.h1
            className="font-cormorant mt-6 leading-[1.05]"
            style={{ fontSize: "clamp(2.2rem, 6.5vw, 5.2rem)" }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.7, ease: easeLuxury }}
          >
            <span className="neon-gold">{get("home.hero_line1")}</span>{" "}
            <span className="italic neon-crimson">{get("home.hero_line2")}</span>
          </motion.h1>

          <motion.p
            className="text-luxury-body text-base sm:text-lg max-w-xl mx-auto mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: easeLuxury }}
          >
            {get("home.hero_text")}
            <span className="text-crimson-bright"> {get("home.hero_tagline")}</span>
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4 mt-12 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25, ease: easeLuxury }}
          >
            <MagneticButton to="/booking" variant="gold" data-testid="hero-book-now">
              <span className="inline-flex items-center gap-2">
                {get("home.cta_primary")} <ChevronRight size={14} />
              </span>
            </MagneticButton>
            <MagneticButton to="/room" variant="ghost" data-testid="hero-explore">
              <span className="inline-flex items-center gap-2">
                <DoorOpen size={14} /> {get("home.cta_secondary")}
              </span>
            </MagneticButton>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="flex items-center justify-center gap-8 mt-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            <div>
              <div className="font-cormorant text-4xl text-gold">
                <CountUp to={5} suffix=".0" duration={2} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">Couples Rating</div>
            </div>
            <div className="w-px h-12 bg-gradient-to-b from-transparent via-gold/30 to-transparent" />
            <div>
              <div className="font-cormorant text-4xl text-gold">
                <CountUp to={100} suffix="%" duration={2.4} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">Discreet</div>
            </div>
            <div className="w-px h-12 bg-gradient-to-b from-transparent via-gold/30 to-transparent hidden sm:block" />
            <div className="hidden sm:block">
              <div className="font-cormorant text-4xl text-gold">24/7</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">Concierge</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          style={{ zIndex: 3 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2 }}
        >
          <div className="flex flex-col items-center gap-2 text-white/40">
            <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
            <ArrowDown size={14} className="animate-scroll-indicator text-gold" />
          </div>
        </motion.div>

        {/* Bottom fade into next section */}
        <div className="absolute bottom-0 inset-x-0 h-40 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent, #070605)", zIndex: 2 }} />
      </section>

      {/* ============ QUICK BOOK STRIP ============ */}
      <section className="relative -mt-16 z-20 px-6 lg:px-12">
        <Reveal y={40}>
          <div
            className="max-w-5xl mx-auto glass-strong p-8 lg:p-10 gold-glow"
            style={{ borderColor: "rgba(201,162,75,0.15)" }}
            data-testid="quick-book-section"
          >
            <div className="grid md:grid-cols-4 gap-6 items-end">
              <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <div className="eyebrow mb-2">Stay Type</div>
                  <div className="text-white font-cormorant text-2xl">Hourly · Overnight</div>
                </div>
                <div>
                  <div className="eyebrow mb-2">From</div>
                  <div className="text-white font-cormorant text-2xl">₹500 <span className="text-sm text-white/40 font-sans">/ hour</span></div>
                </div>
                <div>
                  <div className="eyebrow mb-2">Guests</div>
                  <div className="text-white font-cormorant text-2xl">Couples Only</div>
                </div>
              </div>
              <MagneticButton to="/booking" variant="crimson" data-testid="quick-book-cta" className="text-center">
                Check Availability
              </MagneticButton>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============ MARQUEE ============ */}
      <div className="mt-24">
        <Marquee items={get("home.marquee")} />
      </div>

      {/* ============ WHY US ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div>
            <Reveal>
              <div className="eyebrow">Why Couples Choose Room XIII</div>
              <h2 className="font-cormorant text-4xl sm:text-6xl mt-6 leading-tight">
                A standard above hotels.<br />
                <span className="italic text-crimson-bright">An experience apart.</span>
              </h2>
              <div className="divider-gold" />
              <p className="text-luxury-body max-w-lg">
                We operate one room. One arrival at a time. Every detail — from the lighting
                curve to the linen weight — is engineered for intimacy and indulgence.
              </p>
            </Reveal>

            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">
              {[
                { icon: Shield, t: "Total Discretion", d: "Private entry, soundproof walls, no front-desk awkwardness." },
                { icon: Sparkles, t: "Hospital-Grade Hygiene", d: "Deep-cleaned and sanitized between every stay." },
                { icon: KeyRound, t: "Self Check-in", d: "Smart lock access — your privacy from the first moment." },
                { icon: Heart, t: "Couple-First Design", d: "Mood lighting, dual rain showers, premium add-ons." },
              ].map((item) => (
                <motion.div key={item.t} variants={staggerItem} className="flex gap-4 group">
                  <motion.div
                    className="w-11 h-11 border border-gold/30 flex items-center justify-center shrink-0"
                    whileHover={{ rotate: 90, borderColor: "rgba(201,162,75,0.9)", scale: 1.1 }}
                    transition={{ duration: 0.5, ease: easeLuxury }}
                  >
                    <item.icon size={16} className="text-gold" />
                  </motion.div>
                  <div>
                    <div className="text-white font-medium">{item.t}</div>
                    <div className="text-white/50 text-sm mt-1">{item.d}</div>
                  </div>
                </motion.div>
              ))}
            </StaggerContainer>
          </div>

          <StaggerContainer className="grid grid-cols-2 gap-4" stagger={0.15}>
            {[
              { src: IMG.lounge, h: "h-80", off: "" },
              { src: IMG.kitchen, h: "h-80", off: "mt-12" },
              { src: IMG.bath, h: "h-72", off: "" },
              { src: IMG.g1, h: "h-72", off: "mt-6" },
            ].map((img, i) => (
              <motion.div key={i} variants={staggerItem} className={`img-zoom ${img.off}`}>
                <img src={img.src} alt="Suite detail" className={`w-full ${img.h} object-cover`} />
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ============ AMENITIES STRIP ============ */}
      <section className="border-y border-white/[0.04] py-20" style={{ background: "#0A0908" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12" stagger={0.08}>
            {[
              { n: "Mood Lighting", d: "12 scene presets" },
              { n: "Premium Audio", d: "JBL Bluetooth system" },
              { n: "Smart TV", d: "55″ 4K · Netflix" },
              { n: "Rain Shower", d: "Dual luxury fixtures" },
              { n: "Climate Control", d: "Whisper-quiet AC" },
              { n: "Mini Bar", d: "Complimentary beverages" },
              { n: "Privacy", d: "Soundproof · Discreet entry" },
              { n: "Concierge", d: "24/7 WhatsApp support" },
            ].map((a) => (
              <motion.div
                key={a.n}
                variants={staggerItem}
                whileHover={{ x: 6, borderColor: "#C9A24B" }}
                transition={{ duration: 0.4, ease: easeLuxury }}
                className="border-l border-gold/30 pl-5 cursor-default"
              >
                <div className="font-cormorant text-2xl text-white">{a.n}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/40 mt-2">{a.d}</div>
              </motion.div>
            ))}
          </StaggerContainer>
          <Reveal delay={0.4}>
            <div className="text-center mt-16">
              <MagneticButton to="/amenities" variant="ghost" data-testid="view-amenities-cta">View Full Amenities</MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ YOUR EVENING, SCENE BY SCENE ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="scenes-section">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <div className="eyebrow">The Evening</div>
            <h2 className="font-cormorant text-4xl sm:text-6xl mt-6">Scene by <span className="italic text-crimson-bright">scene</span></h2>
            <div className="divider-gold mx-auto" />
          </div>
        </Reveal>
        <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16" stagger={0.14}>
          {scenes.map((sc) => (
            <motion.div key={sc.n} variants={staggerItem} className="scene-card group relative overflow-hidden spin-border tile-tilt" style={{ minHeight: 420 }}>
              <img src={mediaUrl(sc.img)} alt={sc.t} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,6,5,0.2) 0%, rgba(7,6,5,0.92) 85%)" }} />
              <div className="relative h-full p-7 flex flex-col justify-end" style={{ minHeight: 420 }}>
                <div className="outline-num" style={{ fontSize: "6rem" }}>{sc.n}</div>
                <div className="font-cormorant text-3xl text-white mt-3">{sc.t}</div>
                <p className="text-white/55 text-sm mt-2">{sc.d}</p>
              </div>
            </motion.div>
          ))}
        </StaggerContainer>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="testimonials-section">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <div className="eyebrow">Whispered Approvals</div>
            <h2 className="font-cormorant text-4xl sm:text-6xl mt-6">Loved by couples</h2>
            <div className="divider-gold mx-auto" />
          </div>
        </Reveal>
        <StaggerContainer className="grid md:grid-cols-3 gap-6 mt-16" stagger={0.15}>
          {(!Array.isArray(reviews) || reviews.length === 0) ? (
            <div className="md:col-span-3 text-center text-white/40">No reviews yet.</div>
          ) : reviews.map((r) => (
            <motion.div key={r.id} variants={staggerItem}>
              <TiltCard className="glass p-8 h-full" data-testid={`review-card-${r.id}`}>
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0, rotate: -180 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.08, ease: easeLuxury }}
                    >
                      <Star size={14} className="text-gold fill-gold" />
                    </motion.div>
                  ))}
                </div>
                <div className="font-cormorant text-2xl text-white mb-3">{r.title}</div>
                <p className="text-luxury-body text-sm">{r.comment}</p>
                <div className="mt-6 pt-6 border-t border-white/5 text-xs uppercase tracking-[0.2em] text-gold">
                  — {r.user_name}
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </StaggerContainer>
      </section>

      {/* ============ CINEMATIC CTA ============ */}
      <section className="relative py-40 px-6 lg:px-12 overflow-hidden">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.2 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2, ease: easeLuxury }}
        >
          <img src={mediaUrl(get("home.cta_image"))} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/80" />
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(ellipse at center, rgba(155,27,27,0.18) 0%, transparent 60%)" }}
          />
        </motion.div>
        <Reveal>
          <div className="relative max-w-3xl mx-auto text-center">
            <div className="eyebrow">{get("home.cta_eyebrow")}</div>
            <h2 className="font-cormorant text-4xl sm:text-6xl mt-6 leading-tight">
              {get("home.cta_heading")} <span className="italic text-shine">{get("home.cta_heading_accent")}</span>
            </h2>
            <p className="text-luxury-body mt-8">
              {get("home.cta_text")}
            </p>
            <div className="flex flex-wrap gap-4 mt-10 justify-center">
              <MagneticButton to="/booking" variant="gold" data-testid="cta-book">Book Your Stay</MagneticButton>
              <MagneticButton to="/contact" variant="ghost" data-testid="cta-contact">Concierge</MagneticButton>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
